import { i18n } from "~/localization/i18n";
import { buildItemTypeMap, hasValue, isDateTimeItem, isItemRequired, isReadOnly } from "./form-helpers";
import { createExpressionContext, evaluateExpressionSafe } from "./expression-sandbox";
import { EXPRESSION_SOURCE } from "./design-fault-reporting";
import { isComputationItem } from "./method-types";

const getI18nKey = (ruleName) => `validation.${ruleName}`;

const getI18nMessage = (ruleName, ruleArg) =>
    i18n.t(getI18nKey(ruleName), { [ruleName]: ruleArg });

const customValidators = {
    minLengthArray: (value, ruleName, ruleArg) => {
        /* Only validate the length of the value if a value is selected
          since it should be possible to leave it empty when the item is optional */
        if ((!value && ruleArg) || (value.length && value.length < ruleArg)) {
            return getI18nMessage(ruleName, ruleArg);
        }
    },
    maxLengthArray: (value, ruleName, ruleArg) => {
        if (value && value.length >= ruleArg) {
            return getI18nMessage(ruleName);
        }
    },
    precision: (value, ruleName, ruleArg) => {
        if (!value) return;
        const { allowedDecimals, maxLength } = ruleArg;

        const [integerPart, decimalPart] = value.toString().split(".");

        const hasDecimals = decimalPart?.length > 0;

        if (hasDecimals && allowedDecimals === 0) {
            return getI18nMessage("integer", allowedDecimals);
        }

        if (hasDecimals && allowedDecimals < decimalPart.length) {
            return getI18nMessage("decimal", allowedDecimals);
        }

        const maxIntegerDigits = maxLength - allowedDecimals;

        if (integerPart && integerPart.length > maxIntegerDigits) {
            return allowedDecimals === 0
                ? getI18nMessage("maxDigits", maxIntegerDigits)
                : getI18nMessage("maxDigitsDecimal", maxIntegerDigits);
        }
    },
};

const getRuleName = (ruleName) => {
    if (ruleName === "minValue") {
        return "min";
    }

    if (ruleName === "maxValue") {
        return "max";
    }

    return ruleName;
};

const getRule = (ruleName, ruleArg, ruleKey) => {
    return {
        [ruleName]: {
            value: ruleArg,
            message: getI18nMessage(ruleKey, ruleArg),
        },
    };
};

const getCustomValidator = (ruleName, ruleArg) => (value) =>
    customValidators[ruleName](value, ruleName, ruleArg);

const getCustomRule = (customRuleName, ruleArg) => {
    return {
        validate: {
            [customRuleName]: getCustomValidator(customRuleName, ruleArg),
        },
    };
};

const isInvalidRule = (clinicRuleName, ruleArg, itemType) =>
    ruleArg == null ||
    clinicRuleName === "rangeChecks" || // Handled separately below
    (clinicRuleName === "maxValue" && isDateTimeItem(itemType)); // Is restricted by disabling dates instead

/**
 * Builds RHF validate rules for Hard rangeChecks.
 *
 * Each rule is keyed `rule_hard_<oid>` and closes over `contextRef` so it
 * always reads the latest formData/dependencies/identifierModel at evaluation
 * time (the ref is updated by Form.jsx whenever formData changes).
 *
 * react-hook-form passes the live `formValues` (keyed by oid, same as
 * `formData`) as the validator's second argument. We merge it OVER the
 * contextRef formData so the rule sees values that RHF already knows about but
 * which haven't propagated into contextRef yet. This matters on load: when a
 * default/computed value is written via setValue (immediate in RHF) + dispatch
 * (formData refreshes only on the next render), a validation triggered in
 * between would otherwise read a stale/empty value and wrongly fail the range
 * check for an already-valid default. Non-RHF context (cross-form
 * dependencies, identifierModel, hidden items) keeps coming from contextRef.
 *
 * Returns an empty object when there are no Hard checks.
 *
 * @param {Array} rangeChecks  - item.validation.rangeChecks
 * @param {{ current: { formData, dependencies, identifierModel } }} contextRef
 * @param {Record<string, string>} itemTypes  - OID→itemType map for value typing
 * @param {{ oid: string, label?: string }} item  - names the check's item for
 *   the Designer preview's diagnostics panel; no effect on validation
 * @returns {object} validate rules map
 */
const getHardRangeCheckRules = (rangeChecks, contextRef, itemTypes, item) => {
    const hardChecks = (rangeChecks ?? []).filter((rc) => rc.allowFormSave === false);
    if (hardChecks.length === 0) return {};

    const rules = {};
    for (const rc of hardChecks) {
        rules[`rule_hard_${rc.oid}`] = (value, formValues) => {
            if (!hasValue(value)) return true;

            const { formData, dependencies, identifierModel } = contextRef.current;
            const context = createExpressionContext({
                formData: { ...formData, ...formValues },
                dependencies,
                identifierModel,
                itemTypes,
            });
            const result = evaluateExpressionSafe(rc.expression, context, true, {
                source: EXPRESSION_SOURCE.EDIT_CHECK,
                oid: item?.oid,
                label: item?.label,
            });
            return result ? true : rc.message;
        };
    }
    return rules;
};

const parseRuleArg = (ruleArg) => {
    const parsedValue = Number.parseInt(ruleArg);
    return Number.isInteger(parsedValue) ? parsedValue : ruleArg;
};

const convertItemValidation = (item, contextRef, readOnly, itemTypes) => {
    // Computation items are machine-written, so input-shape rules (precision,
    // min/max, lengths, range checks) never fire on them — the participant
    // cannot edit the value to satisfy them. `required` is the exception: a
    // blank required function means an upstream input is unanswered, which the
    // participant can fix, so it must block Next like any unanswered question
    // instead of letting them reach a Send button that never enables.
    if (isComputationItem(item)) {
        if (isItemRequired(item.validation)) {
            return getRule("required", true, "required");
        }
        return {};
    }

    let rules = Object.entries(item.validation).reduce(
        (rules, [clinicRuleName, ruleArg]) => {
            if (isInvalidRule(clinicRuleName, ruleArg, item.itemType)) {
                return rules;
            }

            const ruleName = getRuleName(clinicRuleName);
            let ruleKey = ruleName;

            // Ensure to capture both empty and invalid number in required number error message
            if (item.itemType === "Number" && ruleName === "required") {
                ruleKey = "required_number";
            }

            const parsedRuleArg = parseRuleArg(ruleArg);

            const isArrayLengthRule =
                item.itemType === "Checkboxes" &&
                (ruleName === "minLength" || ruleName === "maxLength");

            const rule = isArrayLengthRule
                ? getCustomRule(`${ruleName}Array`, parsedRuleArg)
                : getRule(ruleName, parsedRuleArg, ruleKey);

            for (const ruleKey in rule) {
                rules[ruleKey] = rule[ruleKey];
            }

            return rules;
        },
        {}
    );

    if (item.itemType === "Number") {
        rules = {
            ...rules,
            ...getCustomRule("precision", {
                maxLength: 12,
                allowedDecimals: parseRuleArg(item.decimals),
            }),
        };
    }

    // Attach Hard rangeCheck validators when a contextRef is provided.
    // Skipped on read-only forms: dependencies may have drifted since
    // submission, so re-running Hard checks could raise errors that did not
    // exist at submit time (mirrors the Soft-check skip on read-only).
    if (!readOnly && contextRef && item.validation?.rangeChecks?.length) {
        const hardRules = getHardRangeCheckRules(
            item.validation.rangeChecks,
            contextRef,
            itemTypes,
            item
        );
        if (Object.keys(hardRules).length > 0) {
            rules = {
                ...rules,
                validate: {
                    ...rules.validate,
                    ...hardRules,
                },
            };
        }
    }

    return rules;
};

/**
 * Converts validation from Viedoc into react-hook-form compatible rules.
 *
 * @param {object} form
 * @param {{ current: { formData: object, dependencies: object, identifierModel: object } }} [contextRef]
 *   Optional live-reference to the current form context.  When provided, Hard
 *   rangeCheck validators close over this ref so they always read the latest
 *   formData at evaluation time.  The caller (Form.jsx) is responsible for
 *   keeping `contextRef.current` up to date.
 */
export const convertFormValidation = (form, contextRef) => {
    const readOnly = isReadOnly(form.status);
    const itemTypes = buildItemTypeMap(form);
    for (const itemGroup of form.itemGroups) {
        for (const item of itemGroup.items) {
            item.reactHookFormValidation = convertItemValidation(item, contextRef, readOnly, itemTypes);
        }
    }
};
